import type { LocalizedDict } from '../index'

type ConfirmCopy = { title: string; body: string; confirm: string }

export type SampleDetailPartsStrings = {
  loadingBrief: string
  myBrief: string
  saving: string
  locked: string
  autoSaved: string
  productName: string
  productNamePlaceholder: string
  priority: string
  briefPhotos: string
  briefPhotosHintBefore: string
  briefPhotosHintUse: string
  pasteImageUrl: string
  addUrl: string
  dropToAdd: string
  dragHint: string
  main: string
  remove: string
  noPhotos: string
  noPhotosYet: string
  specs: string
  price: string
  producer: string
  targetPrice: string
  buyer: string
  productDetails: string
  productDetailsPlaceholder: string
  productionNotes: string
  privateToYou: string
  saveOpenToFactory: string
  confirmToProduction: string
  backToEditing: string
  markSampleReady: string
  unlock: string
  markAsShipped: string
  sampleReceived: string
  sampleReceivedOverride: string
  trackPackage: string
  deleteSample: string
  handoff: ConfirmCopy
  confirm: ConfirmCopy
  unlockModal: ConfirmCopy
  ready: ConfirmCopy
  priceMissing: string
  priceMissingBody: string
  receivedTitle: string
  receivedBodyShipped: string
  receivedBodyOverride: string
  deleteModal: ConfirmCopy
  etaTitle: string
  etaBody: string
  etaLabel: string
  cancel: string
  confirmButton: string
  shipPlaceholders: { carrier: string; tracking_number: string; tracking_url: string; note: string }
  markShipped: string
  noPriceYet: string
  noProductionNotes: string
  productionNotesPlaceholder: string
  bannerDraftTitle: string
  bannerDraftBody: string
  bannerReadyForFactory: string
  bannerReadOnly: string
  bannerInProduction: string
  expectedCompletion: string
  bannerSampleReady: string
  bannerShipped: string
  carrier: string
  tracking: string
  bannerReceived: string
  customerConfirmed: string
  comments: string
  edited: string
  save: string
  edit: string
  delete: string
  noComments: string
  writeComment: string
  send: string
  activityMovedTo: (phase: string, override: boolean) => string
  activityChangedPriority: string
  activity: string
  noActivity: string
  someone: string
}

export const sampleDetailPartsI18n: LocalizedDict<SampleDetailPartsStrings> = {
  en: {
    loadingBrief: 'Loading brief…',
    myBrief: 'My Brief',
    saving: 'Saving…',
    locked: 'Locked',
    autoSaved: 'Auto-saved',
    productName: 'Product Name',
    productNamePlaceholder: 'e.g. Personalized leather journal',
    priority: 'Priority',
    briefPhotos: 'Brief Photos',
    briefPhotosHintBefore:
      'Photos the factory should use as the spec — your own samples, color/material close-ups, or pulled from a reference with ',
    briefPhotosHintUse: '+ Use',
    pasteImageUrl: 'Paste image URL',
    addUrl: 'Add URL',
    dropToAdd: 'Drop to add',
    dragHint: 'Drag an image here, or paste a URL above',
    main: 'Main',
    remove: 'Remove',
    noPhotos: 'No photos.',
    noPhotosYet: 'No photos yet — paste a URL or pull one from a reference with + Use.',
    specs: 'Specs',
    price: 'Price',
    producer: 'Producer',
    targetPrice: 'Target Price',
    buyer: 'Buyer',
    productDetails: 'Product Details (Buyer)',
    productDetailsPlaceholder: 'Add product details…',
    productionNotes: 'Production Notes (Factory)',
    privateToYou: 'Private to you',
    saveOpenToFactory: 'Save (Open to Factory)',
    confirmToProduction: 'Confirm sample to production',
    backToEditing: 'Back to editing',
    markSampleReady: 'Mark sample as ready',
    unlock: 'Unlock',
    markAsShipped: 'Mark as shipped',
    sampleReceived: 'Sample received',
    sampleReceivedOverride: 'Sample received (override)',
    trackPackage: 'Track package',
    deleteSample: 'Delete sample',
    handoff: {
      title: 'Open to factory?',
      body: "Saving opens the sample to the factory. It's not locked — you can pull it back.",
      confirm: 'Open to factory',
    },
    confirm: {
      title: 'Confirm to production?',
      body: 'This locks the brief and comments. Press Unlock to edit again.',
      confirm: 'Confirm to production',
    },
    unlockModal: {
      title: 'Unlock sample?',
      body: 'This returns the sample to draft so you can edit the brief again.',
      confirm: 'Unlock',
    },
    ready: {
      title: 'Mark sample as ready?',
      body: 'The customer will see the sample is produced and awaiting shipment.',
      confirm: 'Mark ready',
    },
    priceMissing: 'Price missing',
    priceMissingBody: 'Please enter the price information for this sample.',
    receivedTitle: 'Sample received?',
    receivedBodyShipped:
      'Confirms the sample physically arrived at your side. This unlocks adding it to the catalog.',
    receivedBodyOverride:
      'The factory has not marked this sample as shipped. Confirming overrides the factory steps and moves it straight to Received (recorded in the activity log). This unlocks adding it to the catalog.',
    deleteModal: {
      title: 'Delete sample?',
      body: 'The sample will be moved to trash. You can restore it later.',
      confirm: 'Delete',
    },
    etaTitle: 'Confirm sample to production',
    etaBody: 'When do you expect to finish this sample? The customer will see this date.',
    etaLabel: 'Expected completion date',
    cancel: 'Cancel',
    confirmButton: 'Confirm',
    shipPlaceholders: {
      carrier: 'Carrier (e.g. DHL)',
      tracking_number: 'Tracking number',
      tracking_url: 'Tracking URL (optional)',
      note: 'Note (optional)',
    },
    markShipped: 'Mark shipped',
    noPriceYet: 'No price entered yet.',
    noProductionNotes: 'No production notes yet.',
    productionNotesPlaceholder: 'Production notes for this sample…',
    bannerDraftTitle: 'Brief ready?',
    bannerDraftBody: 'Saving opens the sample to the factory. Not locked — you can pull it back.',
    bannerReadyForFactory: 'Ready for factory',
    bannerReadOnly: '— read-only while the factory reviews.',
    bannerInProduction: 'In production',
    expectedCompletion: 'Expected completion:',
    bannerSampleReady: 'Sample ready to ship',
    bannerShipped: 'Shipped',
    carrier: 'Carrier: ',
    tracking: 'Tracking: ',
    bannerReceived: 'Received',
    customerConfirmed: 'The customer confirmed your sample arrived.',
    comments: 'Comments',
    edited: ' · edited',
    save: 'Save',
    edit: 'Edit',
    delete: 'Delete',
    noComments: 'No comments yet.',
    writeComment: 'Write a comment…',
    send: 'Send',
    activityMovedTo: (phase, override) => `moved to ${phase}${override ? ' (override)' : ''}`,
    activityChangedPriority: 'changed priority',
    activity: 'Activity',
    noActivity: 'No activity yet.',
    someone: 'Someone',
  },
  tr: {
    loadingBrief: 'Brif yükleniyor…',
    myBrief: 'Brifim',
    saving: 'Kaydediliyor…',
    locked: 'Kilitli',
    autoSaved: 'Otomatik kaydedildi',
    productName: 'Ürün adı',
    productNamePlaceholder: 'ör. Kişiye özel deri defter',
    priority: 'Öncelik',
    briefPhotos: 'Brif fotoğrafları',
    briefPhotosHintBefore:
      'Fabrikanın örnek alacağı fotoğraflar — kendi numunelerin, renk/malzeme yakın çekimleri ya da bir referanstan şununla aldıkların: ',
    briefPhotosHintUse: '+ Kullan',
    pasteImageUrl: 'Görsel bağlantısını yapıştır',
    addUrl: 'Bağlantı ekle',
    dropToAdd: 'Eklemek için bırak',
    dragHint: 'Bir görseli buraya sürükle ya da yukarıya bağlantı yapıştır',
    main: 'Ana',
    remove: 'Kaldır',
    noPhotos: 'Fotoğraf yok.',
    noPhotosYet: 'Henüz fotoğraf yok — bir bağlantı yapıştır ya da bir referanstan + Kullan ile al.',
    specs: 'Özellikler',
    price: 'Fiyat',
    producer: 'Üretici',
    targetPrice: 'Hedef fiyat',
    buyer: 'Alıcı',
    productDetails: 'Ürün detayları (Alıcı)',
    productDetailsPlaceholder: 'Ürün detaylarını ekle…',
    productionNotes: 'Üretim notları (Fabrika)',
    privateToYou: 'Yalnız sen görürsün',
    saveOpenToFactory: 'Kaydet (Fabrikaya aç)',
    confirmToProduction: 'Numuneyi üretime onayla',
    backToEditing: 'Düzenlemeye dön',
    markSampleReady: 'Numuneyi hazır işaretle',
    unlock: 'Kilidi aç',
    markAsShipped: 'Kargoya verildi işaretle',
    sampleReceived: 'Numune teslim alındı',
    sampleReceivedOverride: 'Numune teslim alındı (atla)',
    trackPackage: 'Kargoyu takip et',
    deleteSample: 'Numuneyi sil',
    handoff: {
      title: 'Fabrikaya açılsın mı?',
      body: 'Kaydedince numune fabrikaya açılır. Kilitlenmez — geri çekebilirsin.',
      confirm: 'Fabrikaya aç',
    },
    confirm: {
      title: 'Üretime onaylansın mı?',
      body: 'Bu işlem brifi ve yorumları kilitler. Yeniden düzenlemek için Kilidi aç’a bas.',
      confirm: 'Üretime onayla',
    },
    unlockModal: {
      title: 'Numunenin kilidi açılsın mı?',
      body: 'Numune taslağa döner, brifi yeniden düzenleyebilirsin.',
      confirm: 'Kilidi aç',
    },
    ready: {
      title: 'Numune hazır işaretlensin mi?',
      body: 'Müşteri numunenin üretildiğini ve kargoyu beklediğini görecek.',
      confirm: 'Hazır işaretle',
    },
    priceMissing: 'Fiyat eksik',
    priceMissingBody: 'Lütfen bu numunenin fiyat bilgisini gir.',
    receivedTitle: 'Numune teslim alındı mı?',
    receivedBodyShipped:
      'Numunenin elinize fiziksel olarak ulaştığını onaylar. Böylece kataloğa eklenebilir.',
    receivedBodyOverride:
      'Fabrika bu numuneyi kargoya verildi olarak işaretlemedi. Onaylarsan fabrika adımları atlanır ve numune doğrudan Teslim alındı’ya geçer (hareket kaydına yazılır). Böylece kataloğa eklenebilir.',
    deleteModal: {
      title: 'Numune silinsin mi?',
      body: 'Numune çöp kutusuna taşınır. Daha sonra geri yükleyebilirsin.',
      confirm: 'Sil',
    },
    etaTitle: 'Numuneyi üretime onayla',
    etaBody: 'Bu numuneyi ne zaman bitirmeyi bekliyorsun? Müşteri bu tarihi görecek.',
    etaLabel: 'Tahmini bitiş tarihi',
    cancel: 'Vazgeç',
    confirmButton: 'Onayla',
    shipPlaceholders: {
      carrier: 'Kargo firması (ör. DHL)',
      tracking_number: 'Takip numarası',
      tracking_url: 'Takip bağlantısı (isteğe bağlı)',
      note: 'Not (isteğe bağlı)',
    },
    markShipped: 'Kargoya verildi',
    noPriceYet: 'Henüz fiyat girilmedi.',
    noProductionNotes: 'Henüz üretim notu yok.',
    productionNotesPlaceholder: 'Bu numune için üretim notları…',
    bannerDraftTitle: 'Brif hazır mı?',
    bannerDraftBody: 'Kaydedince numune fabrikaya açılır. Kilitlenmez — geri çekebilirsin.',
    bannerReadyForFactory: 'Fabrikaya hazır',
    bannerReadOnly: '— fabrika incelerken salt okunur.',
    bannerInProduction: 'Üretimde',
    expectedCompletion: 'Tahmini bitiş:',
    bannerSampleReady: 'Numune kargoya hazır',
    bannerShipped: 'Kargoda',
    carrier: 'Kargo firması: ',
    tracking: 'Takip: ',
    bannerReceived: 'Teslim alındı',
    customerConfirmed: 'Müşteri numunenin ulaştığını onayladı.',
    comments: 'Yorumlar',
    edited: ' · düzenlendi',
    save: 'Kaydet',
    edit: 'Düzenle',
    delete: 'Sil',
    noComments: 'Henüz yorum yok.',
    writeComment: 'Yorum yaz…',
    send: 'Gönder',
    activityMovedTo: (phase, override) => `${phase} aşamasına taşıdı${override ? ' (atlayarak)' : ''}`,
    activityChangedPriority: 'önceliği değiştirdi',
    activity: 'Hareketler',
    noActivity: 'Henüz hareket yok.',
    someone: 'Biri',
  },
}
